import { FC, useEffect, useRef, useState } from 'react';
import {
  FileUploaderDropContainer,
  Modal,
  TextInput,
  TextArea,
  RadioButton,
  RadioButtonGroup,
  InlineNotification,
  Button,
  InlineLoading,
  Select,
  SelectItem,
} from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import {
  MAX_MENTIONS,
  MentionList,
  useMentionAutocomplete,
} from 'components/mention-autocomplete';
import { OncallLine } from './oncall-line';
import { UnsavedChangesModal } from 'components/unsaved-changes-modal';
import { formatBytes } from 'utils/number';
import type { CurrentOncall } from 'types/api-responses';
import {
  SEVERITY_OPTIONS,
  type ReportType,
  type Severity,
} from 'pages/bugs-features/types';
import { submitBugsFeature } from './submit-bugs-feature';
import type { ReportFormError } from './atoms';
import {
  ACCEPTED_TYPES,
  MAX_ATTACHMENT_BYTES,
  useAttachmentUpload,
} from './use-attachment-upload';
import mentionStyles from 'components/mention-autocomplete/mention-autocomplete.module.scss';
import styles from './report-form.module.scss';

export interface ReportFormModalProps {
  open: boolean;
  initialReportType: ReportType;
  /**
   * Set when the form was opened from the error page. Recorded with the report and shown
   * to the reporter so they know what is being sent, but not editable — it is evidence,
   * not prose.
   */
  crash?: ReportFormError | null;
  /** This week's on-call engineer, read while the form is open. */
  oncall: CurrentOncall;
  onClose: () => void;
  /** Called after a successful submit so the underlying table can refetch. */
  onSubmitted: (reportType: ReportType) => void;
}

/** Mirrors BugsFeature's `validates :title, length: { maximum: 255 }`. */
const TITLE_LIMIT = 255;

const DESCRIPTION_HELP: Record<ReportType, string> = {
  bug: 'What did you expect to happen, and what happened instead? Steps to reproduce help most.',
  feature: 'What are you trying to do, and what would make it easier?',
};

export const ReportFormModal: FC<ReportFormModalProps> = ({
  open,
  initialReportType,
  crash = null,
  oncall,
  onClose,
  onSubmitted,
}) => {
  const [reportType, setReportType] = useState<ReportType>(initialReportType);

  // useState only reads its argument on first mount, and ReportFormHost keeps this modal
  // mounted at the app shell for the whole session — so without this, whichever type the
  // form first opened on would be the type it opened on forever. That was invisible while
  // one button always asked for 'bug'; the two header icons made it wrong.
  //
  // Keyed on `open` so the type is adopted each time the form is opened, and a user who
  // switches the radio mid-edit is not overridden while they are typing.
  useEffect(() => {
    if (open) setReportType(initialReportType);
  }, [open, initialReportType]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const {
    results: mentionResults,
    highlighted,
    setHighlighted,
    insert: insertMention,
    mentions,
    resetMentions,
    syncCaret,
    close: closeMentions,
    lookupFailed,
  } = useMentionAutocomplete(description, setDescription);

  // The dropdown takes the arrow keys and Enter only while it is showing something —
  // otherwise Enter has to keep breaking the line, which matters more in a six-row
  // description than any mention shortcut.
  const onDescriptionKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (mentionResults.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((highlighted + 1) % mentionResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(
        (highlighted - 1 + mentionResults.length) % mentionResults.length
      );
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      insertMention(mentionResults[highlighted]);
    } else if (event.key === 'Escape') {
      // Dismissed without choosing, so the person can type an @ that is not a mention.
      // The capture listener below is what actually gets this key; this stays because it is
      // the branch that runs if Carbon ever stops intercepting, and reading the handler
      // should not suggest Escape is unhandled here.
      event.preventDefault();
      closeMentions();
    }
  };

  /*
    Escape has to be taken before Carbon sees it.

    Modal installs a keydown listener on `document` in the CAPTURE phase, stops propagation
    and calls onRequestClose for any Escape — so the textarea's own handler, which is a React
    synthetic listener in the bubble phase, never ran. Pressing Escape to dismiss the mention
    list closed the whole modal and handleClose's reset() discarded the title, the
    description, the severity and every attachment. A person typing "@jo", deciding they
    meant a literal @, and hitting Escape lost the report they had just written.

    Registered on `window`, not on document, and that is the whole trick. Two capture
    listeners on the same node fire in registration order, and Carbon's goes on at mount —
    so a document listener added later still loses. Capture descends window → document, so
    this one runs first wherever it was added, and stopPropagation keeps the key from
    reaching Carbon at all.

    One key, and only while the list is actually open. Everything else, including Escape with
    no list showing, is left to Carbon: closing the modal on Escape is the right default and
    is not ours to take away.
  */
  useEffect(() => {
    if (mentionResults.length === 0) return undefined;

    const takeEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      closeMentions();
    };

    window.addEventListener('keydown', takeEscape, true);

    return () => window.removeEventListener('keydown', takeEscape, true);
  }, [mentionResults.length, closeMentions]);

  // A crash can name itself, so the title starts filled. Only the title: the description
  // asks what the person was doing, which is the one thing the browser cannot know and the
  // one thing that makes the report worth triaging — prefilling it would put words in
  // their mouth and get a report full of them.
  //
  // Sliced to the column's limit. maxLength below caps *typing*, not a programmatic set, and
  // a crash names itself at whatever length it likes — a React hydration error runs well past
  // 255 characters. Submitting then failed validation on a field the reporter could not have
  // over-typed, against a limit nothing on screen had mentioned. error-page.tsx already does
  // this for the stack trace; the title had been left out.
  //
  // Same `open` keying as the type above, and only when the field is untouched, so
  // reopening does not overwrite something half-typed.
  useEffect(() => {
    if (!open || !crash?.message) return;

    setTitle((current) => current || crash.message.slice(0, TITLE_LIMIT));
  }, [open, crash]);
  // Defaults to Sev-3 rather than blank. Self-assessed severity skews high, and an
  // unset field forces triage to guess; starting at the least severe means raising
  // it is a deliberate act.
  const [severity, setSeverity] = useState<Severity>('sev_3');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const {
    attachments,
    addFiles,
    remove,
    reset: resetAttachments,
    signedIds,
    isUploading,
    canUpload,
  } = useAttachmentUpload();

  // Read at render rather than from a router hook so this component stays usable
  // from the app header, outside any route context.
  const isOnReportsPage =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/feedback');

  /**
   * Paste a screenshot straight into the form.
   *
   * The reason this matters: the fastest way to capture a bug on a Mac is Cmd-Shift-Ctrl-4,
   * which puts the image on the clipboard and never on disk — so without this the reporter
   * has to save it somewhere first purely to satisfy a file picker.
   *
   * Only intercepts when the clipboard actually carries files, so pasting text into the
   * title or description behaves exactly as before.
   *
   * @param {React.ClipboardEvent<HTMLDivElement>} event The paste event from the form body.
   * @return {void}
   */
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!canUpload) return;

    const pasted = Array.from(event.clipboardData?.files ?? []);
    if (pasted.length === 0) return;

    event.preventDefault();
    addFiles(pasted);
  };

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('sev_3');
    setError(null);
    setIsSubmitting(false);
    resetAttachments();
    // Cleared with everything else. Harmless while this modal remounts between uses, but it
    // is the one piece of form state this function used to leave behind, and a stale name
    // resolving against the next report's text is not a failure anyone would go looking for.
    resetMentions();
  };

  /**
   * Whether there is anything here worth asking about before it goes.
   *
   * The description and the attachments, and deliberately not the title: on the crash path
   * the title is prefilled by us rather than typed by them, so counting it would prompt
   * everyone who opened the form and immediately thought better of it.
   */
  const hasUnsavedWork =
    description.trim().length > 0 || attachments.length > 0;

  /*
    Read through a ref, because Carbon captures onRequestClose once and never refreshes it.

    Modal's Escape listener is registered in a useEffect keyed on [open] alone —
    onRequestClose is deliberately not a dependency, with the exhaustive-deps rule disabled
    and carbon#20452 cited. So the handler Escape reaches is the one from the render in which
    the modal opened, and any state it closes over is that render's: description was '' there,
    so the guard below read "nothing to lose" however much had been typed since, and Escape
    discarded the report exactly as it used to. Cancel worked throughout, because a button's
    onClick is re-read every render — which is what made this worth a test rather than a look.

    The ref object is stable, so reading .current inside the captured function gets the
    current value. Assigned during render rather than in an effect, because Escape can arrive
    before an effect has run for the keystroke that changed it.
  */
  const hasUnsavedWorkRef = useRef(hasUnsavedWork);
  hasUnsavedWorkRef.current = hasUnsavedWork;

  const keepEditing = () => setIsConfirmingDiscard(false);

  const discard = () => {
    setIsConfirmingDiscard(false);
    reset();
    onClose();
  };

  /*
    Escape and Cancel both land here, and both used to wipe the form outright.

    This is the form people reach at the moment something has just broken, from the error
    page — the description is the one account of the failure nobody else can reconstruct, and
    an attachment is a recording they waited on. Losing that to a keystroke meant recording
    and writing it again, from memory, about a crash that had already happened.

    Escape still closes the modal. The comment on the mention interceptor above is right that
    closing on Escape is the correct default and not ours to take away; what was wrong was
    doing it silently. That interceptor still takes Escape while the mention list is open, so
    dismissing the dropdown never reaches this.

    The question is components/unsaved-changes-modal, the same one components/navigation-guard
    asks when you navigate away from an unsaved form, so someone who has seen it once should
    recognise this.
  */
  const handleClose = () => {
    if (hasUnsavedWorkRef.current) {
      setIsConfirmingDiscard(true);
      return;
    }

    discard();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('A title and description are both required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitBugsFeature(
        {
          reportType,
          title: title.trim(),
          description: description.trim(),
          mentions,
          // Feature requests carry no severity — Jira's field is bug triage.
          severity: reportType === 'bug' ? severity : null,
          crash,
        },
        signedIds
      );
      reset();
      onSubmitted(reportType);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit');
      setIsSubmitting(false);
    }
  };

  /*
    Returned instead of the form, not rendered beside it. Two open Carbon modals share
    z-index 9000 and stack in document order, so a form rendered after this painted over
    it and could not be closed. The form's state lives on this component, so declining
    the question re-renders it as it was; only discard() clears it.

    A different component from the form's Modal on purpose. Returning a bare Modal here
    reconciled as the same instance as the form's (a top-level fragment is unwrapped), and
    Carbon registers its Escape listener once per change of `open`, which never changes, so
    Escape on the question kept running the form's handleClose and re-asked. A different
    type is a real remount with its own listener.
  */
  if (open && isConfirmingDiscard) {
    return (
      <UnsavedChangesModal
        message="This report has not been submitted. Discarding it loses what you have written and any files you attached."
        onKeep={keepEditing}
        onDiscard={discard}
      />
    );
  }

  return (
    <Modal
      open={open}
      modalHeading="Report a bug or request a feature"
      primaryButtonText={isSubmitting ? 'Submitting…' : 'Submit'}
      secondaryButtonText="Cancel"
      // Blocked while a file is still transferring: submitting now would drop
      // that attachment silently, since only completed uploads have a signed id.
      primaryButtonDisabled={isSubmitting || isUploading}
      // The body is taller than the modal once the attachment picker is showing, and
      // without this Carbon clips it mid-sentence rather than scrolling — the file size
      // limits were cut in half at the bottom edge.
      hasScrollingContent
      // Cancel on the question mounts this again, and Carbon's initial focus would land on
      // the close button, where Enter out of habit re-asks the question. The description is
      // also the right first stop on the crash path, where the title is already filled.
      selectorPrimaryFocus="#bugs-feature-description"
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={handleClose}
    >
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not submit"
          subtitle={error}
          hideCloseButton
          className={styles.notification}
        />
      )}

      {/*
        Who will pick this up. While the lookup is in flight the line's height is held with a
        blank one, so the fields below do not jump when the name arrives; a neutral line when
        there is no answer, because the report reaches triage the same way whether or not we
        can name the person.
      */}
      {oncall.state === 'loading' && (
        <p className={styles.oncall} aria-hidden="true">
          &nbsp;
        </p>
      )}
      {oncall.state === 'ok' && <OncallLine oncall={oncall} />}
      {oncall.state === 'unavailable' && (
        <p className={styles.oncall}>
          Your report goes to the on-call engineer for triage.
        </p>
      )}

      <div onPaste={handlePaste}>
        <RadioButtonGroup
          legendText="Type"
          name="report-type"
          valueSelected={reportType}
          onChange={(value) => setReportType(value as ReportType)}
          className={styles.formField}
        >
          <RadioButton labelText="Bug" value="bug" id="report-type-bug" />
          <RadioButton
            labelText="Feature request"
            value="feature"
            id="report-type-feature"
          />
        </RadioButtonGroup>

        <TextInput
          id="bugs-feature-title"
          labelText="Title"
          placeholder="Short summary"
          value={title}
          maxLength={TITLE_LIMIT}
          // Shown rather than left to be discovered by a rejected submit: nothing on this
          // form said there was a limit, and the one other counter in the app (the AFE
          // catalog's notes field) pairs it with the same slice-on-set.
          enableCounter
          maxCount={TITLE_LIMIT}
          onChange={(e) => setTitle(e.target.value)}
          className={styles.formField}
        />

        {/*
      Bugs only. Jira's Severity field is a triage input for defects; a feature
      request has no meaningful value for it, and sending one would put noise
      into the board's severity reporting.
    */}
        {reportType === 'bug' && (
          <>
            <Select
              id="bugs-feature-severity"
              labelText="Severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity)}
              className={styles.formField}
            >
              {SEVERITY_OPTIONS.map((option) => (
                <SelectItem
                  key={option.id}
                  value={option.id}
                  text={option.label}
                />
              ))}
            </Select>
            <p className={styles.severityHint}>
              {SEVERITY_OPTIONS.find((o) => o.id === severity)?.description}{' '}
              Triage may adjust this.
            </p>
          </>
        )}

        {/*
        The description takes @-mentions, the same as a comment does.
        
        It used to be a plain textarea, which meant the one moment a reporter most wants to
        pull somebody in — "@jason this is the export bug you saw" — was the one place the
        feature did not work. Typing a name there notified nobody and reached Jira as text.
      */}
        <div className={`${styles.formField} ${styles.mentionAnchor}`}>
          <TextArea
            id="bugs-feature-description"
            labelText="Description"
            helperText={DESCRIPTION_HELP[reportType]}
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onSelect={(e: React.SyntheticEvent<HTMLTextAreaElement>) =>
              syncCaret(e.currentTarget.selectionStart ?? 0)
            }
            onClick={(e: React.MouseEvent<HTMLTextAreaElement>) =>
              syncCaret(e.currentTarget.selectionStart ?? 0)
            }
            onKeyDown={onDescriptionKeyDown}
            onBlur={closeMentions}
          />

          <MentionList
            results={mentionResults}
            lookupFailed={lookupFailed}
            highlighted={highlighted}
            onHighlight={setHighlighted}
            onSelect={insertMention}
          />

          {/*
            Said before the submit, not after it. The write path refuses text over this
            limit, and being refused for something nothing on screen had mentioned is the
            trap the title's character limit used to set.
          */}
          {mentions.length > MAX_MENTIONS && (
            <p className={mentionStyles.unresolved}>
              <span className={mentionStyles.unresolvedName}>
                {mentions.length} people tagged
              </span>{' '}
              — reduce to {MAX_MENTIONS} or lower to submit, and tag the people
              who need to act on this. The rest will be able to see it on the
              report.
            </p>
          )}
        </div>

        {/*
      Shown only when the captured page is worth mentioning — i.e. when the form
      was opened from somewhere else in the app. Opened from the reports page
      itself the captured URL is /feedback, which tells a triager nothing,
      and a note about browser details alone is just noise above the Submit
      button. Context is still captured either way; this line exists to explain
      it when it matters, not as a disclaimer.
    */}
        {canUpload && (
          <div className={styles.formField}>
            <span className={styles.attachmentLabel}>
              Screenshots or recordings (optional)
            </span>

            {/*
          Carbon's drop container rather than a hidden input behind a button: it takes a
          drag-and-drop and still opens the picker on click or Enter, so the keyboard and
          screen-reader path the old button provided is kept rather than traded away.
          Same component the Add Document modal uses.
        */}
            <FileUploaderDropContainer
              labelText="Drag and drop files here, or click to browse"
              accept={ACCEPTED_TYPES.split(',')}
              multiple
              onAddFiles={(
                _event: unknown,
                { addedFiles }: { addedFiles: File[] }
              ) => addFiles(Array.from(addedFiles ?? []))}
            />

            {attachments.length > 0 && (
              <ul className={styles.attachmentList}>
                {attachments.map((file) => (
                  <li key={file.localId} className={styles.attachmentItem}>
                    <span className={styles.attachmentName} title={file.name}>
                      {file.name}
                    </span>
                    <span className={styles.attachmentMeta}>
                      {file.status === 'uploading' && (
                        <InlineLoading description="Uploading…" />
                      )}
                      {file.status === 'complete' && formatBytes(file.byteSize)}
                      {file.status === 'error' && (
                        <span className={styles.attachmentError}>
                          {file.error}
                        </span>
                      )}
                    </span>
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={TrashCan}
                      iconDescription={`Remove ${file.name}`}
                      tooltipPosition="left"
                      onClick={() => remove(file.localId)}
                    />
                  </li>
                ))}
              </ul>
            )}

            <p className={styles.contextNote}>
              Images, video and PDFs, up to{' '}
              {MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB each.
            </p>
          </div>
        )}

        {!isOnReportsPage && (
          <p className={styles.contextNote}>
            The page you were on and your browser details are attached
            automatically.
          </p>
        )}
      </div>
    </Modal>
  );
};
